/** Pinch — radial deformation pulling pixels toward or pushing them
 *  away from the image center.
 *
 *  For each output pixel at normalized radius `r ∈ [0, 1]` from the
 *  center, we sample the source at `r^(1 + k)` where `k` is the
 *  user's strength setting:
 *
 *    - `k > 0` (pinch): `r^(>1)` < r → source is FURTHER from center
 *      → output pulls pixels INWARD, creating a "sucked into the
 *      middle" look.
 *    - `k < 0` (bulge): `r^(<1)` > r → source is CLOSER to center
 *      → output pushes pixels OUTWARD, creating a "spherized" look.
 *
 *  Falls back smoothly to identity at `k = 0`. Bilinear sampling
 *  for sub-pixel accuracy. */

import { bilinearSample } from './_bilinear';

export interface PinchValue {
  /** Strength (-100..+100). Positive = pinch inward; negative =
   *  bulge outward. 0 = identity. */
  amount: number;
}

export const identityPinchValue = (): PinchValue => ({ amount: 0 });

/** Inner edge of the smoothing band that blends the pinch warp to
 *  the un-warped source as `r` approaches 1.0. Without it, the
 *  inscribed-half-diagonal-circle boundary shows a visible
 *  discontinuity at high amounts (the warp math cuts across the
 *  frame on non-square images).
 *
 *  Exported so the GPU shader interpolates this exact value into
 *  its source at compile time — guarantees the GPU and JS paths
 *  share the band geometry and can't drift apart. */
export const PINCH_SMOOTH_INNER = 0.9;

export function applyPinchPixels(source: ImageData, value: PinchValue): ImageData {
  const w = source.width;
  const h = source.height;
  const amount = Math.max(-100, Math.min(100, value.amount));
  if (amount === 0) {
    return new ImageData(new Uint8ClampedArray(source.data), w, h);
  }
  // Map -100..+100 → exponent that ranges from 0.5 (full bulge) to
  // 2.0 (full pinch). Identity at k=0 → exponent=1.
  const exponent = amount > 0 ? 1 + amount / 100 : 1 / (1 - amount / 100);
  const cx = (w - 1) / 2;
  const cy = (h - 1) / 2;
  const halfDiag = Math.sqrt(cx * cx + cy * cy);
  const src = source.data;
  const out = new Uint8ClampedArray(src.length);
  // Scratch buffer for the smoothing region — bilinearSample writes
  // four bytes; we lerp those against the source pixel before writing
  // to `out`. Allocated once outside the loop so the hot path stays
  // allocation-free.
  const scratch = new Uint8ClampedArray(4);
  // See `PINCH_SMOOTH_INNER` export above for the rationale on the
  // smoothing band — the constant lives outside this function so the
  // GPU shader can pin to the same value.
  const SMOOTH_INNER = PINCH_SMOOTH_INNER;
  const SMOOTH_RANGE = 1 - SMOOTH_INNER;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const dx = (x - cx) / halfDiag;
      const dy = (y - cy) / halfDiag;
      const r = Math.sqrt(dx * dx + dy * dy);
      const di = (y * w + x) * 4;
      if (r === 0) {
        // Center pixel — undefined direction. Copy source center.
        bilinearSample(src, w, h, cx, cy, out, di);
        continue;
      }
      // Outside the unit circle — pass through cleanly. Pinch math
      // explodes at large `r` so we cap effect at the inscribed
      // half-diagonal.
      if (r >= 1) {
        out[di] = src[di];
        out[di + 1] = src[di + 1];
        out[di + 2] = src[di + 2];
        out[di + 3] = src[di + 3];
        continue;
      }
      const rWarped = Math.pow(r, exponent);
      const scale = rWarped / r;
      const sx = cx + dx * scale * halfDiag;
      const sy = cy + dy * scale * halfDiag;
      if (r > SMOOTH_INNER) {
        // Smoothing band — lerp pinched ↔ source via smoothstep so the
        // r=1 boundary produces an output equal to the cleanly-passed-
        // through r=1.0001 neighbor (no visible seam).
        bilinearSample(src, w, h, sx, sy, scratch, 0);
        const t = (r - SMOOTH_INNER) / SMOOTH_RANGE;
        const blend = t * t * (3 - 2 * t); // smoothstep
        const inv = 1 - blend;
        out[di] = scratch[0] * inv + src[di] * blend;
        out[di + 1] = scratch[1] * inv + src[di + 1] * blend;
        out[di + 2] = scratch[2] * inv + src[di + 2] * blend;
        out[di + 3] = scratch[3] * inv + src[di + 3] * blend;
      } else {
        bilinearSample(src, w, h, sx, sy, out, di);
      }
    }
  }
  return new ImageData(out, w, h);
}

export function applyPinchToCanvas(
  source: ImageData,
  target: HTMLCanvasElement,
  value: PinchValue,
): void {
  const tCtx = target.getContext('2d');
  if (!tCtx) return;
  if (target.width !== source.width || target.height !== source.height) {
    target.width = source.width;
    target.height = source.height;
  }
  tCtx.putImageData(applyPinchPixels(source, value), 0, 0);
}
